import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ModalHeader, closeOnlyViaButton, modalHeaderBtnSx } from '../chatMessageStyles';
import { AudioPlayer, LoadingIndicator, SUMMARY_LOADING_STEPS } from '../AudioSummaryPanel';
import AreDisclaimer from '../AreDisclaimer';
import { renderCard } from './api';
import { CardIcon, WaveIcon, blockSpec, lengthLabel, scheduleLabel } from './cardModel';

/**
 * One own audio card, opened from its circle: the player of the fixed cards
 * (AudioSummaryPanel's AudioPlayer + text toggle + refresh), the card's
 * recipe in one line, and the three ways to change it — with Bro, by hand,
 * or delete (undo on the home screen).
 */
export default function AudioCardPlayer({ card, catalog, language, onClose, onEditVoice, onEditForm, onDelete }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true, render: null, error: '' });
  const [showText, setShowText] = useState(false);

  const load = useCallback(async (refresh) => {
    setState({ loading: true, render: null, error: '' });
    try {
      const res = await renderCard(card.id, language, refresh);
      setState({ loading: false, render: res.render, error: '' });
    } catch (e) {
      setState({ loading: false, render: null, error: e.message });
    }
  }, [card.id, language]);

  useEffect(() => { load(false); }, [load]);

  const render = state.render;
  const prepared = render?.created_at ? new Date(render.created_at) : null;
  const preparedText = prepared && render.origin === 'schedule'
    ? t('audio_prepared_at', { time: prepared.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' }) })
    : '';
  const area = card.area_label || '';

  return (
    <Dialog
      open
      onClose={closeOnlyViaButton(onClose)}
      maxWidth={false}
      fullWidth
      sx={{ '& .MuiDialog-container': { alignItems: 'center', justifyContent: 'center' } }}
      PaperProps={{ sx: { borderRadius: '14px', m: 2, maxHeight: 'calc(100% - 32px)', width: '100%', maxWidth: 720 } }}
    >
      <Box sx={{ p: 2.5, overflowY: 'auto' }}>
        <ModalHeader icon={<CardIcon name={card.icon} />} title={card.name} onClose={onClose} />

        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.25 }}>
          {[area, scheduleLabel(catalog, card.schedule), lengthLabel(catalog, card.length)].filter(Boolean).join(' · ')}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', opacity: 0.75, mb: 1.5 }}>
          {(card.blocks || []).map((b) => blockSpec(catalog, b.key)?.label || b.key).join(' · ')}
        </Typography>

        {state.error ? (
          <Typography variant="body2" color="error" sx={{ py: 1.5 }}>{state.error}</Typography>
        ) : (
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, visibility: state.loading ? 'hidden' : 'visible' }}>
              <Box sx={{ flex: 1 }}>
                <AudioPlayer key={render?.id || 'placeholder'} src={render?.audio ? `data:audio/mp3;base64,${render.audio}` : ''}
                  card={card.name} length={card.length} />
              </Box>
              <IconButton onClick={() => setShowText(!showText)} size="small" title={t('showText') || 'Show text'}
                sx={{ color: showText ? 'primary.main' : 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'rgba(25, 208, 239, 0.06)' } }}>
                <DescriptionIcon sx={{ fontSize: 18 }} />
              </IconButton>
              {render && (
                <IconButton onClick={() => load(true)} size="small" title={t('refresh') || 'Refresh'}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'rgba(25, 208, 239, 0.06)' } }}>
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
            {state.loading && <LoadingIndicator steps={SUMMARY_LOADING_STEPS} />}
          </Box>
        )}
        {render && !render.audio && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.75 }}>{t('audio_no_voice')}</Typography>
        )}
        {preparedText && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', opacity: 0.7, mt: 0.75 }}>{preparedText}</Typography>
        )}

        <Collapse in={showText && !!render?.text}>
          <Box sx={{ mt: 1.5, px: 2, py: 1.5, borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.05)', fontSize: '13px', lineHeight: 1.7, color: 'text.secondary', whiteSpace: 'pre-line' }}>
            {render?.text}
          </Box>
        </Collapse>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 2.5 }}>
          <Button variant="contained" onClick={onEditVoice} startIcon={<WaveIcon size={16} />}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('audio_change_with_bro')}
          </Button>
          <Button onClick={onEditForm} size="small" startIcon={<EditIcon sx={{ fontSize: 16 }} />}
            sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}>
            {t('audio_change_by_hand')}
          </Button>
          <Button onClick={onDelete} size="small" startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
            sx={{ ...modalHeaderBtnSx, ml: 'auto', px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600,
              '&:hover': { color: 'error.main', borderColor: 'error.main', bgcolor: 'rgba(239, 68, 68, 0.08)' } }}>
            {t('audio_delete')}
          </Button>
        </Box>
        <AreDisclaimer sx={{ mt: 2, justifyContent: 'flex-start' }} />
      </Box>
    </Dialog>
  );
}
